/**
 * Parse the subset of gettext PO used by the app catalogs into a msgid → msgstr map.
 *
 * Header records (empty msgid) are ignored. Catalogs use stable dotted keys and
 * separate singular/count keys, so contexts and plural records are intentionally
 * outside this parser's scope.
 */
export function unescapePoString(value: string): string {
  return value.replace(/\\(.)/g, (_match: string, escaped: string) => {
    if (escaped === "n") return "\n";
    if (escaped === "t") return "\t";
    if (escaped === "r") return "\r";
    if (escaped === '"') return '"';
    if (escaped === "\\") return "\\";
    return escaped;
  });
}

export function parsePo(source: string): Record<string, string> {
  const catalog: Record<string, string> = {};
  let msgid: string | null = null;
  let msgstr: string | null = null;
  let field: "msgid" | "msgstr" | null = null;

  const flush = () => {
    // Unfinished gettext entries are stored as msgid with an empty msgstr.
    // Leave them out so lookup can fall back to English instead of a blank UI.
    if (msgid && msgstr?.trim()) {
      catalog[msgid] = msgstr;
    }
    msgid = null;
    msgstr = null;
    field = null;
  };

  for (const rawLine of source.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith("#")) continue;

    const msgidMatch = line.match(/^msgid\s+"(.*)"\s*$/);
    if (msgidMatch) {
      if (field === "msgstr") flush();
      field = "msgid";
      msgid = unescapePoString(msgidMatch[1]);
      continue;
    }

    const msgstrMatch = line.match(/^msgstr\s+"(.*)"\s*$/);
    if (msgstrMatch) {
      field = "msgstr";
      msgstr = unescapePoString(msgstrMatch[1]);
      continue;
    }

    const continued = line.match(/^"(.*)"\s*$/);
    if (continued && field === "msgid") {
      msgid = `${msgid ?? ""}${unescapePoString(continued[1])}`;
    } else if (continued && field === "msgstr") {
      msgstr = `${msgstr ?? ""}${unescapePoString(continued[1])}`;
    }
  }

  flush();
  return catalog;
}
